import React, { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { IconButton, Textarea } from "../../atoms";
import { useLeadNotes, useCreateNote, useDeleteNote } from "../../hooks/useLeads";
import { useTeamMembers, formatMember } from "../../hooks/useTeamMembers";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import { resolveUserAvatar } from "../../../../utils/userAvatar";

function formatRelative(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(d).toLocaleDateString();
}

function initialsFromName(name, email) {
  const source = (name || "").trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (email) return email.split("@")[0].slice(0, 2).toUpperCase();
  return "?";
}

export default function NotesPanel({ leadId, focusItemId, onFocusResolved }) {
  const [body, setBody] = useState("");
  const [error, setError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const { data, isLoading } = useLeadNotes(leadId);
  const createNote = useCreateNote(leadId);
  const deleteNote = useDeleteNote(leadId);
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });
  const { members } = useTeamMembers();
  const memberByEmail = React.useMemo(() => {
    const map = new Map();
    members.forEach((m) => { if (m.email) map.set(m.email, m); });
    return map;
  }, [members]);
  const notes = data?.notes || [];

  const confirmNote = confirmId ? notes.find((n) => n._id === confirmId) : null;

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setError(null);
    try {
      await createNote.mutateAsync({ body: text });
      setBody("");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Save failed");
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <style>{FOCUS_ROW_STYLE}</style>
      {/* Composer */}
      <div>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Write a note about this lead…"
          rows={3}
          style={{ minHeight: 72 }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8 }}>
          {/* <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
            ⌘+Enter to save
          </div> */}
          <button
            onClick={submit}
            disabled={createNote.isPending || !body.trim()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: body.trim() ? "var(--crm-primary)" : "var(--color-bg-muted)",
              color: body.trim() ? "var(--crm-primary-text, #fff)" : "var(--color-text-secondary)",
              border: "none",
              borderRadius: 8,
              padding: "7px 14px",
              fontSize: 13,
              fontWeight: 500,
              cursor: createNote.isPending || !body.trim() ? "not-allowed" : "pointer",
              transition: "background 120ms ease",
            }}
          >
            {createNote.isPending && <Loader2 size={13} className="animate-spin" />}
            Save note
          </button>
        </div>
        {error && <div style={{ fontSize: 12, color: "var(--crm-danger)", marginTop: 6 }}>{error}</div>}
      </div>

      {/* List */}
      <div>
        {isLoading && <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Loading…</div>}
        {!isLoading && notes.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", textAlign: "center", padding: "24px 0", fontStyle: "italic" }}>
            No notes yet
          </div>
        )}
        {notes.map((n) => {
          const member = n.authorEmail ? memberByEmail.get(n.authorEmail) : null;
          const displayName = member ? formatMember(member) : (n.authorEmail || "unknown");
          const authorAvatar = member ? resolveUserAvatar(member) : null;
          return (
          <div ref={register(n._id)} key={n._id} style={{ display: "flex", gap: 12, padding: "14px 0", borderBottom: "1px solid var(--color-border-subtle)" }}>
            {authorAvatar ? (
              <img src={authorAvatar} alt="" title={n.authorEmail || undefined} style={{ width: 28, height: 28, borderRadius: 9999, flexShrink: 0, objectFit: "cover" }} />
            ) : (
            <div
              title={n.authorEmail || undefined}
              style={{
                width: 28, height: 28, borderRadius: 9999, flexShrink: 0,
                background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 600,
              }}
            >
              {initialsFromName(member ? formatMember(member) : null, n.authorEmail)}
            </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                  <span
                    style={{ color: "var(--color-text-primary)", fontWeight: 500 }}
                    title={n.authorEmail || undefined}
                  >
                    {displayName}
                  </span>
                  {" · "}
                  {formatRelative(n.createdAt)}
                </div>
                <IconButton
                  size="sm"
                  aria-label="Delete note"
                  onClick={() => setConfirmId(n._id)}
                  disabled={deleteNote.isPending}
                >
                  <Trash2 size={13} />
                </IconButton>
              </div>
              <div style={{ fontSize: 13, color: "var(--color-text-primary)", whiteSpace: "pre-wrap", lineHeight: "20px" }}>
                {n.body}
              </div>
            </div>
          </div>
          );
        })}
      </div>
      <ConfirmDeleteModal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => {
          if (!confirmId) return;
          deleteNote.mutate(confirmId, {
            onSuccess: () => setConfirmId(null),
          });
        }}
        title="Delete this note?"
        message={confirmNote?.body ? `"${confirmNote.body.slice(0, 100)}${confirmNote.body.length > 100 ? "…" : ""}"` : "This note will be removed from the lead."}
        confirmLabel="Delete note"
        isPending={deleteNote.isPending}
      />
    </div>
  );
}
